import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import SectionActions from './SectionActions';
import PrintHeader from './PrintHeader';
import {
  FileText, Plus, Loader2, Trash2, Edit, Search, Euro, Calendar,
  CheckCircle2, AlertTriangle, Clock, CreditCard, Building2, Upload,
  Camera, Eye, Download, Mail, MessageCircle
} from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const FattureForni = () => {
  const [fatture, setFatture] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('tutte');
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [expandedFattura, setExpandedFattura] = useState(null);
  const [newF, setNewF] = useState({ supplier: '', number: '', amount: '', date: '', dueDate: '', description: '', cantiereId: '', tipo: 'fattura_ricevuta' });
  const [newFile, setNewFile] = useState(null);
  const [destinazione, setDestinazione] = useState('magazzino');
  const [materiali, setMateriali] = useState([]);
  const [showPay, setShowPay] = useState(null);
  const [payData, setPayData] = useState({ importo: '', data: '', metodo: '', banca: '', note: '', isParziale: false });
  const [tipoTab, setTipoTab] = useState('fattura_ricevuta');
  const [editingF, setEditingF] = useState(null);
  const [editFData, setEditFData] = useState({ supplier: '', number: '', amount: '', date: '', dueDate: '', description: '' });
  const [editMateriali, setEditMateriali] = useState([]);
  // Scan
  const [scanning, setScanning] = useState(false);
  const pageRef = useRef(null);

  const loadData = async () => {
    try {
      const [f, c] = await Promise.all([api.getFattureForni(), api.getCantieri()]);
      setFatture(f); setCantieri(c);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  const today = new Date().toISOString().split('T')[0];
  const daPagare = fatture.filter(f => f.status === 'da_pagare');
  const scadute = daPagare.filter(f => f.dueDate && f.dueDate < today);
  const inScadenza = daPagare.filter(f => {
    if (!f.dueDate) return false;
    const diff = (new Date(f.dueDate) - new Date()) / 86400000;
    return diff >= 0 && diff <= 7;
  });
  const totaleDaPagare = daPagare.reduce((s, f) => s + (f.amount || 0), 0);

  const filtered = fatture.filter(f => {
    const fTipo = f.tipo || 'fattura_ricevuta';
    if (fTipo !== tipoTab) return false;
    const matchFilter = filter === 'tutte' || 
      (filter === 'da_pagare' && (f.status === 'da_pagare' || f.status === 'parziale')) || 
      (filter === 'pagate' && f.status === 'pagata') || 
      (filter === 'scadute' && f.dueDate && f.dueDate < today && (f.status === 'da_pagare' || f.status === 'parziale')) || 
      (filter === 'parziale' && f.status === 'parziale');
    const matchSearch = !search || f.supplier?.toLowerCase().includes(search.toLowerCase()) || f.number?.includes(search);
    return matchFilter && matchSearch;
  });

  const handleAdd = async () => {
    if (!newF.supplier) return;
    try {
      if (materiali.length > 0) {
        // Create fattura with materials → auto-add to magazzino/cantiere
        await api.createFatturaConMateriali({
          ...newF, amount: parseFloat(newF.amount) || 0, tipo: newF.tipo,
          materiali: materiali.map(m => ({ nome: m.nome, quantita: parseFloat(m.quantita) || 0, unita: m.unita, prezzo_cadauno: parseFloat(m.prezzo_cad) || 0, prezzo_totale: parseFloat(m.prezzo_tot) || 0 })),
          destinazione
        });
      } else if (newFile) {
        await api.uploadFatturaForni(newF.supplier, newF.number, parseFloat(newF.amount) || 0, newF.date, newF.dueDate, newF.description, newF.cantiereId, newFile);
      } else {
        await api.createFatturaForni({ ...newF, amount: parseFloat(newF.amount) || 0 });
      }
      setNewF({ supplier: '', number: '', amount: '', date: '', dueDate: '', description: '', cantiereId: '', tipo: tipoTab });
      setNewFile(null); setMateriali([]); setDestinazione('magazzino'); setShowAdd(false); loadData();
    } catch (e) { console.error(e); }
  };

  const handlePay = async () => {
    if (!showPay || !payData.data) return;
    try {
      const importo = parseFloat(payData.importo) || showPay.amount;
      if (payData.isParziale) {
        await api.addPagamentoParziale(showPay.id, { importo, data: payData.data, metodo: payData.metodo, banca: payData.banca, note: payData.note });
      } else {
        await api.updateFatturaForni(showPay.id, { status: 'pagata', paymentMethod: payData.metodo, paymentBank: payData.banca, paymentDate: payData.data, pagato_parziale: showPay.amount });
      }
      setShowPay(null); setPayData({ importo: '', data: '', metodo: '', banca: '', note: '', isParziale: false }); loadData();
    } catch (e) { console.error(e); }
  };

  const openEditFattura = (f) => {
    setEditFData({ supplier: f.supplier || '', number: f.number || '', amount: String(f.amount || ''), date: f.date || '', dueDate: f.dueDate || '', description: f.description || '' });
    setEditMateriali((f.materiali || []).map(m => ({
      nome: m.nome || m.name || '', quantita: String(m.quantita || m.quantity || ''),
      unita: m.unita || m.unit || 'pz', prezzo_cad: String(m.prezzo_cadauno || m.prezzo_cad || m.prezzo_unitario || ''),
      prezzo_tot: String(m.prezzo_totale || m.prezzo_tot || '')
    })));
    setEditingF(f);
  };

  const handleSaveEditFattura = async () => {
    if (!editingF) return;
    try {
      await api.updateFatturaForni(editingF.id, { supplier: editFData.supplier, number: editFData.number, amount: parseFloat(editFData.amount) || 0, date: editFData.date, dueDate: editFData.dueDate, description: editFData.description });
      if (editMateriali.length > 0) {
        await api.updateFatturaMateriali(editingF.id, editMateriali.map(m => ({ nome: m.nome, quantita: parseFloat(m.quantita) || 0, unita: m.unita, prezzo_cadauno: parseFloat(m.prezzo_cad) || 0, prezzo_totale: parseFloat(m.prezzo_tot) || 0 })));
      }
      setEditingF(null); loadData();
    } catch (e) { console.error(e); }
  };

  const handleScanAdd = async (file) => {
    if (!file) return;
    setScanning(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ocr-ddt?cantiereId=none`, {
        method: 'POST', body: formData
      });
      const text = await resp.text();
      let result;
      try { result = JSON.parse(text); } catch { result = { success: false, error: 'Risposta non valida dal server' }; }
      
      if (result && result.success && result.extracted) {
        const ext = result.extracted;
        setNewF({ supplier: ext.fornitore || '', number: ext.numero_ddt || '', amount: String(ext.totale || ''), date: ext.data || today, dueDate: '', description: (ext.materiali || []).map(m => m.nome).join(', '), cantiereId: '' });
        setMateriali((ext.materiali || []).map(m => ({
          nome: m.nome || '', quantita: String(m.quantita || ''), unita: m.unita || 'pz',
          prezzo_cad: String(m.prezzo_cadauno || m.prezzo_unitario || ''), prezzo_tot: String(m.prezzo_totale || '')
        })));
        setShowAdd(true);
      } else {
        alert('Scansione completata ma nessun dato estratto. Prova con una foto più chiara o inserisci manualmente.');
        setShowAdd(true);
      }
    } catch (e) {
      alert('Errore di connessione. Inserisci i dati manualmente.');
      setShowAdd(true);
    }
    setScanning(false);
  };

  if (loading) return <div className="page-container"><div className="loading-center"><Loader2 size={32} className="spin" /></div></div>;

  return (
    <div className="page-container" ref={pageRef}>
      <PrintHeader title="Fatture Fornitori" subtitle={`${daPagare.length} da pagare — €${totaleDaPagare.toFixed(2)}`} />
      <div className="page-header">
        <div className="header-left">
          <h1 className="page-title">Fatture Fornitori</h1>
          <p className="page-subtitle">Gestione fatture, scadenze e pagamenti</p>
        </div>
        <div className="header-right"><SectionActions sectionTitle="Fatture Fornitori" contentRef={pageRef} /></div>
      </div>

      {/* Alerts */}
      {scadute.length > 0 && (
        <div className="fatt-alert fatt-alert-red">
          <AlertTriangle size={20} />
          <div><strong>{scadute.length} fatture SCADUTE!</strong><span> — €{scadute.reduce((s, f) => s + f.amount, 0).toFixed(2)} da pagare</span></div>
        </div>
      )}
      {inScadenza.length > 0 && (
        <div className="fatt-alert fatt-alert-amber">
          <Clock size={20} />
          <div><strong>{inScadenza.length} fatture in scadenza</strong><span> nei prossimi 7 giorni — €{inScadenza.reduce((s, f) => s + f.amount, 0).toFixed(2)}</span></div>
        </div>
      )}

      {/* Tipo Tabs */}
      <div className="cantiere-filter" style={{ padding: '0 28px 8px' }}>
        {[['fattura_ricevuta','Fatture Ricevute'],['nota_credito','Note Credito'],['fattura_emessa','Fatture Emesse']].map(([k,l]) => (
          <button key={k} className={`filter-chip ${tipoTab === k ? 'filter-chip-active' : ''}`} onClick={() => setTipoTab(k)}>{l} ({fatture.filter(f => (f.tipo || 'fattura_ricevuta') === k).length})</button>
        ))}
      </div>

      {/* Actions */}
      <div className="mag-action-row">
        <button className="big-action-btn" style={{ flex: 1 }} onClick={() => { setShowAdd(true); setNewF({ supplier: '', number: '', amount: '', date: today, dueDate: '', description: '', cantiereId: '', tipo: tipoTab }); setNewFile(null); setMateriali([]); }}>
          <Plus size={20} /> {tipoTab === 'fattura_emessa' ? 'Nuova Fattura Emessa' : tipoTab === 'nota_credito' ? 'Nuova Nota Credito' : 'Aggiungi Fattura'}
        </button>
        <label className="big-action-btn" style={{ flex: 1, borderColor: '#8b5cf6', color: '#8b5cf6', cursor: 'pointer' }}>
          {scanning ? <><Loader2 size={20} className="spin" /> Scansione AI...</> : <><Camera size={20} /> Scansiona Fattura (AI)</>}
          <input type="file" accept="image/*,.pdf,.jpg,.jpeg,.png,.heic" capture="environment" style={{ display: 'none' }} disabled={scanning} onChange={e => { if (e.target.files[0]) handleScanAdd(e.target.files[0]); e.target.value = ''; }} />
        </label>
      </div>

      {/* Stats */}
      <div className="presenze-stats">
        <div className="pstat"><span className="pstat-value" style={{ color: '#ef4444' }}>{scadute.length}</span><span className="pstat-label">Scadute</span></div>
        <div className="pstat"><span className="pstat-value" style={{ color: '#f59e0b' }}>{inScadenza.length}</span><span className="pstat-label">In Scadenza</span></div>
        <div className="pstat"><span className="pstat-value" style={{ color: '#3b82f6' }}>{daPagare.length}</span><span className="pstat-label">Da Pagare</span></div>
        <div className="pstat"><span className="pstat-value" style={{ color: '#ef4444' }}>€{totaleDaPagare.toFixed(2)}</span><span className="pstat-label">Totale Da Pagare</span></div>
        <div className="pstat"><span className="pstat-value" style={{ color: '#22c55e' }}>{fatture.filter(f => f.status === 'pagata').length}</span><span className="pstat-label">Pagate</span></div>
      </div>

      {/* Filters */}
      <div className="presenze-filters">
        <div className="search-bar" style={{ maxWidth: '300px' }}>
          <Search size={18} className="search-icon" /><Input placeholder="Cerca fornitore o numero..." value={search} onChange={e => setSearch(e.target.value)} className="search-input" />
        </div>
        <div className="cantiere-filter">
          {['tutte', 'da_pagare', 'parziale', 'scadute', 'pagate'].map(f => (
            <button key={f} className={`filter-chip ${filter === f ? 'filter-chip-active' : ''}`} onClick={() => setFilter(f)}>
              {f === 'tutte' ? 'Tutte' : f === 'da_pagare' ? 'Da Pagare' : f === 'parziale' ? 'Parziali' : f === 'scadute' ? 'Scadute' : 'Pagate'}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <div className="fatt-list">
        {filtered.map(f => {
          const isScaduta = f.dueDate && f.dueDate < today && f.status === 'da_pagare';
          const cantiere = cantieri.find(c => c.id === f.cantiereId);
          return (
            <div key={f.id} className={`fatt-card ${isScaduta ? 'fatt-scaduta' : f.status === 'pagata' ? 'fatt-pagata' : ''}`}>
              <div className="fatt-row-main" onClick={() => setExpandedFattura(expandedFattura === f.id ? null : f.id)} style={{ cursor: 'pointer' }}>
              <div className="fatt-left">
                <FileText size={20} color={f.status === 'pagata' ? '#22c55e' : isScaduta ? '#ef4444' : '#f59e0b'} />
                <div>
                  <h4 className="fatt-supplier">{f.supplier}</h4>
                  <p className="fatt-detail">N. {f.number || '-'} {f.description && `— ${f.description}`}</p>
                  {cantiere && <p className="fatt-cantiere"><Building2 size={11} /> {cantiere.name}</p>}
                  {f.materiali?.length > 0 && <p style={{ fontSize: 11, color: '#3b82f6', margin: '2px 0 0' }}>📦 {f.materiali.length} materiali — clicca per vedere</p>}
                </div>
              </div>
              <div className="fatt-center">
                <span className="fatt-amount">
                  €{(f.pagato_parziale > 0 ? ((f.amount || 0) - (f.pagato_parziale || 0)) : (f.amount || 0)).toFixed(2)}
                  {f.pagato_parziale > 0 && <span style={{ fontSize: 11, color: '#9ca3af', display: 'block' }}>(Totale: €{f.amount?.toFixed(2)})</span>}
                </span>
                <span className="fatt-dates">
                  <Calendar size={11} /> {f.date ? new Date(f.date).toLocaleDateString('it-IT') : '-'}
                  {f.dueDate && <> → <strong>{new Date(f.dueDate).toLocaleDateString('it-IT')}</strong></>}
                </span>
              </div>
              <div className="fatt-right">
                <span className={`fatt-status ${f.status === 'pagata' ? 'fatt-st-ok' : f.status === 'parziale' ? 'fatt-st-warning' : isScaduta ? 'fatt-st-late' : 'fatt-st-pending'}`}>
                  {f.status === 'pagata' ? `PAGATA ${f.paymentDate ? new Date(f.paymentDate).toLocaleDateString('it-IT') : ''}` : f.status === 'parziale' ? 'PARZIALE' : isScaduta ? 'SCADUTA' : 'DA PAGARE'}
                </span>
                {f.status === 'pagata' && f.paymentMethod && <span className="fatt-pay-info">{f.paymentMethod} {f.paymentBank && `— ${f.paymentBank}`}</span>}
              </div>
              <div className="fatt-actions">
                {(f.status === 'da_pagare' || f.status === 'parziale') && (
                  <button className="fatt-pay-btn" onClick={(e) => { e.stopPropagation(); setShowPay(f); setPayData({ importo: '', data: today, metodo: '', banca: '', note: '', isParziale: false }); }}>
                    <CheckCircle2 size={14} /> {f.status === 'parziale' ? 'Paga Rata' : 'Paga'}
                  </button>
                )}
                {f.filename && <button className="doc-share-btn doc-btn-view" onClick={(e) => { e.stopPropagation(); window.open(api.getUploadUrl(f.filename), '_blank'); }}><Eye size={13} /></button>}
                <button className="mag-action-btn-sm" onClick={(e) => { e.stopPropagation(); openEditFattura(f); }} title="Modifica"><Edit size={13} /></button>
                <button className="pa-delete" onClick={(e) => { e.stopPropagation(); api.deleteFatturaForni(f.id).then(loadData); }}><Trash2 size={13} /></button>
              </div>
              </div>
              {/* Materiali espansi */}
              {expandedFattura === f.id && f.materiali?.length > 0 && (
                <div className="fatt-materiali">
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead><tr style={{ background: '#f8fafc' }}>
                      <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontSize: 10, fontWeight: 600, color: '#6b7280' }}>Materiale</th>
                      <th style={{ padding: '6px 10px', textAlign: 'center', borderBottom: '1px solid #e5e7eb', fontSize: 10 }}>Qtà</th>
                      <th style={{ padding: '6px 10px', textAlign: 'center', borderBottom: '1px solid #e5e7eb', fontSize: 10 }}>Unità</th>
                      <th style={{ padding: '6px 10px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontSize: 10 }}>€/Cad</th>
                      <th style={{ padding: '6px 10px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontSize: 10 }}>Totale</th>
                    </tr></thead>
                    <tbody>
                      {f.materiali.map((m, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '5px 10px', fontWeight: 500 }}>{m.nome || m.name || ''}</td>
                          <td style={{ padding: '5px 10px', textAlign: 'center' }}>{m.quantita || m.quantity || 0}</td>
                          <td style={{ padding: '5px 10px', textAlign: 'center', color: '#6b7280' }}>{m.unita || m.unit || 'pz'}</td>
                          <td style={{ padding: '5px 10px', textAlign: 'right' }}>€{parseFloat(m.prezzo_cadauno || m.prezzo_cad || m.prezzo_unitario || 0).toFixed(2)}</td>
                          <td style={{ padding: '5px 10px', textAlign: 'right', fontWeight: 600 }}>€{parseFloat(m.prezzo_totale || m.prezzo_tot || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {f.destinazione && <p style={{ padding: '8px 10px', fontSize: 11, color: '#6b7280', margin: 0 }}>Destinazione: <strong>{f.destinazione === 'magazzino' ? 'Magazzino' : cantieri.find(c => c.id === f.destinazione)?.name || f.destinazione}</strong></p>}
                </div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && <p className="empty-text">Nessuna fattura. Aggiungi la prima!</p>}
      </div>

      {/* Add Dialog */}
      {showAdd && (
        <Dialog open={showAdd} onOpenChange={setShowAdd}><DialogContent style={{ maxHeight: '90vh', overflowY: 'auto' }}>
          <DialogHeader><DialogTitle>Nuova Fattura Fornitore</DialogTitle></DialogHeader>
          <div className="form-fields">
            <div className="form-field"><Label>Fornitore *</Label><Input value={newF.supplier} onChange={e => setNewF({...newF, supplier: e.target.value})} placeholder="Nome fornitore" /></div>
            <div className="form-row">
              <div className="form-field"><Label>Numero Fattura</Label><Input value={newF.number} onChange={e => setNewF({...newF, number: e.target.value})} /></div>
              <div className="form-field"><Label>Importo (€)</Label><Input type="number" step="0.01" value={newF.amount} onChange={e => setNewF({...newF, amount: e.target.value})} /></div>
            </div>
            <div className="form-row">
              <div className="form-field"><Label>Data Fattura</Label><Input type="date" value={newF.date} onChange={e => setNewF({...newF, date: e.target.value})} /></div>
              <div className="form-field"><Label>Scadenza Pagamento</Label><Input type="date" value={newF.dueDate} onChange={e => setNewF({...newF, dueDate: e.target.value})} /></div>
            </div>
            <div className="form-field"><Label>Cantiere (opzionale)</Label>
              <Select value={newF.cantiereId} onValueChange={v => setNewF({...newF, cantiereId: v})}><SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                <SelectContent>{cantieri.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></div>
            <div className="form-field"><Label>Descrizione</Label><Input value={newF.description} onChange={e => setNewF({...newF, description: e.target.value})} /></div>

            {/* MATERIALI */}
            <div style={{ background: '#f0fdf4', borderRadius: 10, padding: 14, border: '1px solid #86efac' }}>
              <p style={{ fontSize: 12, fontWeight: 600, color: '#16a34a', margin: '0 0 8px' }}>MATERIALI DELLA FATTURA</p>
              {materiali.map((m, i) => (
                <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Input style={{ flex: 3 }} placeholder="Materiale" value={m.nome} onChange={e => { const u = [...materiali]; u[i].nome = e.target.value; setMateriali(u); }} />
                  <Input style={{ flex: 1 }} type="number" placeholder="Qtà" value={m.quantita} onChange={e => { const u = [...materiali]; u[i].quantita = e.target.value; setMateriali(u); }} />
                  <Input style={{ flex: 1 }} placeholder="Unità" value={m.unita} onChange={e => { const u = [...materiali]; u[i].unita = e.target.value; setMateriali(u); }} />
                  <Input style={{ flex: 1 }} type="number" placeholder="€/cad" value={m.prezzo_cad} onChange={e => { const u = [...materiali]; u[i].prezzo_cad = e.target.value; setMateriali(u); }} />
                  <button type="button" style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }} onClick={() => setMateriali(materiali.filter((_, j) => j !== i))}><Trash2 size={14} /></button>
                </div>
              ))}
              <button type="button" className="dcb-add-btn" style={{ color: '#16a34a', borderColor: '#86efac' }} onClick={() => setMateriali([...materiali, { nome: '', quantita: '', unita: 'pz', prezzo_cad: '', prezzo_tot: '' }])}>
                <Plus size={13} /> Aggiungi Materiale
              </button>
            </div>

            {/* DESTINAZIONE */}
            {materiali.length > 0 && (
              <div style={{ background: '#eff6ff', borderRadius: 10, padding: 14, border: '1px solid #bfdbfe' }}>
                <p style={{ fontSize: 12, fontWeight: 600, color: '#2563eb', margin: '0 0 8px' }}>DOVE VANNO I MATERIALI?</p>
                <Select value={destinazione} onValueChange={setDestinazione}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="magazzino">Magazzino</SelectItem>
                    {cantieri.map(c => <SelectItem key={c.id} value={c.id}>Cantiere: {c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p style={{ fontSize: 11, color: '#6b7280', margin: '6px 0 0' }}>I materiali verranno automaticamente caricati nella destinazione scelta</p>
              </div>
            )}

            <div className="form-field"><Label>Allega File</Label>
              <input type="file" accept=".pdf,.jpg,.png,.heic" onChange={e => setNewFile(e.target.files[0])} className="form-file-input" /></div>
            <button className="form-submit" onClick={handleAdd} disabled={!newF.supplier}>
              {materiali.length > 0 ? `Registra Fattura + ${materiali.length} Materiali` : 'Registra Fattura'}
            </button>
          </div>
        </DialogContent></Dialog>
      )}

      {/* Pay Dialog */}
      {showPay && (
        <Dialog open={!!showPay} onOpenChange={() => setShowPay(null)}><DialogContent>
          <DialogHeader><DialogTitle>Registra Pagamento — {showPay.supplier}</DialogTitle></DialogHeader>
          <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 4px' }}>Fattura N. {showPay.number} — Totale: <strong>€{showPay.amount?.toFixed(2)}</strong></p>
          {showPay.pagato_parziale > 0 && <p style={{ fontSize: 13, color: '#f59e0b', margin: '0 0 4px' }}>Già pagato: €{showPay.pagato_parziale?.toFixed(2)} — Rimanenza: €{(showPay.amount - showPay.pagato_parziale).toFixed(2)}</p>}
          {showPay.pagamenti_parziali?.length > 0 && (
            <div style={{ background: '#f8fafc', borderRadius: 8, padding: 8, marginBottom: 8 }}>
              <p style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', margin: '0 0 4px' }}>RATE PRECEDENTI:</p>
              {showPay.pagamenti_parziali.map((p, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, fontSize: 12, padding: '3px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <span style={{ fontWeight: 600 }}>€{p.importo?.toFixed(2)}</span>
                  <span style={{ color: '#6b7280' }}>{p.data ? new Date(p.data + 'T12:00').toLocaleDateString('it-IT') : ''}</span>
                  <span style={{ color: '#9ca3af' }}>{p.metodo}</span>
                </div>
              ))}
            </div>
          )}
          <div className="form-fields">
            <div className="backup-toggle-row">
              <span>Pagamento parziale (a rate)?</span>
              <button className={`backup-toggle ${payData.isParziale ? 'backup-toggle-on' : ''}`} onClick={() => setPayData(p => ({...p, isParziale: !p.isParziale}))} style={{ fontSize: 10 }}>
                {payData.isParziale ? 'SÌ' : 'NO'}
              </button>
            </div>
            {payData.isParziale && (
              <>
                <div className="form-field">
                  <Label>Importo Rata (€) *</Label>
                  <Input type="number" step="0.01" value={payData.importo} onChange={e => setPayData(p => ({...p, importo: e.target.value}))} placeholder={`Rimanenza: €${((showPay.amount || 0) - (showPay.pagato_parziale || 0)).toFixed(2)}`} />
                  {payData.importo && (
                    <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                      Dopo questo pagamento rimarranno: <strong style={{ color: '#f59e0b' }}>€{Math.max(0, ((showPay.amount || 0) - (showPay.pagato_parziale || 0) - parseFloat(payData.importo || 0))).toFixed(2)}</strong>
                    </p>
                  )}
                </div>
              </>
            )}
            <div className="form-field"><Label>Metodo *</Label>
              <Select value={payData.metodo} onValueChange={v => setPayData(p => ({...p, metodo: v}))}>
                <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Bonifico Bancario">Bonifico Bancario</SelectItem>
                  <SelectItem value="Contanti">Contanti</SelectItem>
                  <SelectItem value="Assegno">Assegno</SelectItem>
                  <SelectItem value="Carta di Credito">Carta di Credito</SelectItem>
                  <SelectItem value="RiBa">RiBa</SelectItem>
                  <SelectItem value="Compensazione">Compensazione</SelectItem>
                </SelectContent>
              </Select></div>
            <div className="form-field"><Label>Banca / Dettaglio</Label><Input value={payData.banca} onChange={e => setPayData(p => ({...p, banca: e.target.value}))} placeholder="Es: Intesa San Paolo" /></div>
            <div className="form-field"><Label>Data Pagamento *</Label><Input type="date" value={payData.data} onChange={e => setPayData(p => ({...p, data: e.target.value}))} /></div>
            {payData.isParziale && <div className="form-field"><Label>Note</Label><Input value={payData.note} onChange={e => setPayData(p => ({...p, note: e.target.value}))} placeholder="Es: Rata 1 di 3" /></div>}
            <button className="form-submit" style={{ background: '#22c55e' }} onClick={handlePay} disabled={!payData.data}>
              {payData.isParziale ? 'Registra Rata' : 'Conferma Pagamento Totale'}
            </button>
          </div>
        </DialogContent></Dialog>
      )}

      {/* Edit Fattura Dialog */}
      {editingF && (
        <Dialog open={!!editingF} onOpenChange={() => setEditingF(null)}>
          <DialogContent style={{ maxHeight: '90vh', overflowY: 'auto' }}>
            <DialogHeader><DialogTitle>Modifica Fattura — {editingF.supplier}</DialogTitle></DialogHeader>
            <div className="form-fields">
              <div className="form-row">
                <div className="form-field"><Label>Fornitore</Label><Input value={editFData.supplier} onChange={e => setEditFData(p => ({...p, supplier: e.target.value}))} /></div>
                <div className="form-field"><Label>Numero</Label><Input value={editFData.number} onChange={e => setEditFData(p => ({...p, number: e.target.value}))} /></div>
              </div>
              <div className="form-row">
                <div className="form-field"><Label>Importo (€)</Label><Input type="number" step="0.01" value={editFData.amount} onChange={e => setEditFData(p => ({...p, amount: e.target.value}))} /></div>
                <div className="form-field"><Label>Data</Label><Input type="date" value={editFData.date} onChange={e => setEditFData(p => ({...p, date: e.target.value}))} /></div>
              </div>
              <div className="form-field"><Label>Scadenza</Label><Input type="date" value={editFData.dueDate} onChange={e => setEditFData(p => ({...p, dueDate: e.target.value}))} /></div>
              <div className="form-field"><Label>Descrizione</Label><Input value={editFData.description} onChange={e => setEditFData(p => ({...p, description: e.target.value}))} /></div>

              {/* Materiali */}
              <div style={{ background: '#f0fdf4', borderRadius: 10, padding: 14, border: '1px solid #86efac' }}>
                <p style={{ fontSize: 12, fontWeight: 600, color: '#16a34a', margin: '0 0 8px' }}>MATERIALI</p>
                {editMateriali.map((m, i) => (
                  <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Input style={{ flex: 3, minWidth: 120 }} placeholder="Materiale" value={m.nome} onChange={e => { const u = [...editMateriali]; u[i] = {...u[i], nome: e.target.value}; setEditMateriali(u); }} />
                    <Input style={{ flex: 1, minWidth: 60 }} type="number" placeholder="Qtà" value={m.quantita} onChange={e => { const u = [...editMateriali]; u[i] = {...u[i], quantita: e.target.value}; setEditMateriali(u); }} />
                    <Input style={{ flex: 1, minWidth: 50 }} placeholder="Unità" value={m.unita} onChange={e => { const u = [...editMateriali]; u[i] = {...u[i], unita: e.target.value}; setEditMateriali(u); }} />
                    <Input style={{ flex: 1, minWidth: 60 }} type="number" step="0.01" placeholder="€/cad" value={m.prezzo_cad} onChange={e => { const u = [...editMateriali]; u[i] = {...u[i], prezzo_cad: e.target.value, prezzo_tot: String((parseFloat(e.target.value) || 0) * (parseFloat(u[i].quantita) || 0))}; setEditMateriali(u); }} />
                    <Input style={{ flex: 1, minWidth: 70 }} type="number" step="0.01" placeholder="Totale" value={m.prezzo_tot} onChange={e => { const u = [...editMateriali]; u[i] = {...u[i], prezzo_tot: e.target.value}; setEditMateriali(u); }} />
                    <button type="button" style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }} onClick={() => setEditMateriali(editMateriali.filter((_, j) => j !== i))}><Trash2 size={14} /></button>
                  </div>
                ))}
                <button type="button" className="dcb-add-btn" style={{ color: '#16a34a', borderColor: '#86efac' }} onClick={() => setEditMateriali([...editMateriali, { nome: '', quantita: '', unita: 'pz', prezzo_cad: '', prezzo_tot: '' }])}>
                  <Plus size={13} /> Aggiungi Materiale
                </button>
              </div>

              <button className="form-submit" onClick={handleSaveEditFattura}>Salva Modifiche</button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default FattureForni;
